import React from 'react';
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';

import {
  DEFAULT_NEW_CALENDAR_MODE,
  DEFAULT_NEW_CALENDAR_PERIOD,
  NEW_CALENDAR_MODES,
  NEW_CALENDAR_PERIODS,
  NEW_CALENDAR_VIEWS,
} from './newCalendar.config';
import {
  useCalendarEvents,
  useCalendarFilters,
  useCalendarLocale,
  useCalendarModals,
} from './hooks';
import { getDateInTargetMonth } from './newCalendar.helpers';
import { readCalendarUiState, saveCalendarUiState } from './calendarSessionState';

function getViewForModeAndPeriod(mode, period) {
  return NEW_CALENDAR_VIEWS[mode]?.[period] ?? NEW_CALENDAR_VIEWS[DEFAULT_NEW_CALENDAR_MODE][DEFAULT_NEW_CALENDAR_PERIOD];
}

function getModeAndPeriodForView(viewType) {
  for (const [mode, periodViews] of Object.entries(NEW_CALENDAR_VIEWS)) {
    for (const [period, currentViewType] of Object.entries(periodViews)) {
      if (currentViewType === viewType) {
        return { mode, period };
      }
    }
  }

  return {
    mode: DEFAULT_NEW_CALENDAR_MODE,
    period: DEFAULT_NEW_CALENDAR_PERIOD,
  };
}

export function useNewCalendarController(language) {
  const token = sessionStorage.getItem('token') || null;
  const calendarPath = window.location.pathname;
  const savedCalendarState = React.useMemo(() => readCalendarUiState(calendarPath), [calendarPath]);
  const launchParams = React.useMemo(() => new URLSearchParams(window.location.search), []);
  const requestedMode = launchParams.get('mode');
  const requestedPeriod = launchParams.get('period');
  const requestedMemberId = launchParams.get('memberId');
  const initialMode = Object.values(NEW_CALENDAR_MODES).includes(requestedMode)
    ? requestedMode
    : Object.values(NEW_CALENDAR_MODES).includes(savedCalendarState?.activeMode)
      ? savedCalendarState.activeMode
      : DEFAULT_NEW_CALENDAR_MODE;
  const initialPeriod = Object.values(NEW_CALENDAR_PERIODS).includes(requestedPeriod)
    ? requestedPeriod
    : Object.values(NEW_CALENDAR_PERIODS).includes(savedCalendarState?.activePeriod)
      ? savedCalendarState.activePeriod
      : DEFAULT_NEW_CALENDAR_PERIOD;
  const initialSelectedDate = React.useMemo(() => {
    const restoredDate = dayjs(savedCalendarState?.selectedDate);
    return restoredDate.isValid() ? restoredDate : dayjs();
  }, [savedCalendarState?.selectedDate]);
  const fullCalendarRef = React.useRef(null);
  const [selectedDate, setSelectedDate] = React.useState(initialSelectedDate);
  const [currentRangeLabel, setCurrentRangeLabel] = React.useState('');
  const [activeMode, setActiveMode] = React.useState(initialMode);
  const [activePeriod, setActivePeriod] = React.useState(initialPeriod);

  const getWeekStart = React.useCallback((date) => {
    const currentDate = dayjs(date);
    const diffFromMonday = (currentDate.day() + 6) % 7;
    return currentDate.subtract(diffFromMonday, 'day').startOf('day');
  }, []);

  const [visibleRange, setVisibleRange] = React.useState(() => {
    const initialWeekStart = getWeekStart(initialSelectedDate);

    return {
      start: initialWeekStart.toDate(),
      end: initialWeekStart.add(7, 'day').toDate(),
    };
  });

  const decodedToken = React.useMemo(() => {
    if (!token) return null;

    try {
      return jwtDecode(token);
    } catch (_error) {
      return null;
    }
  }, [token]);

  const { adapterLocale, localeText } = useCalendarLocale(language);
  const {
    categories,
    catChecks,
    handleCategoryCheckChange,
    onlyWithHacs,
    setOnlyWithHacs,
    setShowSystemTasks,
    showSystemTasks,
    advancedFilters,
    setAdvancedFilters,
    pmas,
    members,
  } = useCalendarFilters(token, savedCalendarState?.filters);
  React.useEffect(() => {
    if (!requestedMemberId) return;
    setOnlyWithHacs(true);
    setAdvancedFilters((currentFilters) => ({
      ...currentFilters,
      memberIds: [requestedMemberId],
    }));
  }, [requestedMemberId, setAdvancedFilters, setOnlyWithHacs]);
  const {
    closeModalTask,
    closeModalTaskView,
    closeRemoveModal,
    handleEditFromView,
    handleRemoveFromView,
    modalOpenTask,
    modalOpenTaskView,
    openCreateTask,
    openRemoveModal,
    openTaskView,
    removeEntityId,
    task,
    taskView,
    taskViewMember,
  } = useCalendarModals(calendarPath);
  const {
    badgeDays,
    badgeLoading,
    calendarEvents,
    confirmEventCompletion,
    executeDeleteTask,
    refreshCalendar,
    rollbackEventCompletion,
    startEventCompletion,
  } = useCalendarEvents({
    token,
    selectedDate,
    visibleRange,
    onlyWithHacs,
    showSystemTasks,
    catChecks,
    advancedFilters,
  });

  const navigateCalendarToDate = React.useCallback((date) => {
    const nextDate = dayjs(date);
    if (!nextDate.isValid()) return;

    setSelectedDate(nextDate);
    const calendarApi = fullCalendarRef.current?.getApi?.();
    if (calendarApi) calendarApi.gotoDate(nextDate.toDate());
  }, []);

  const handleDateChange = React.useCallback((newValue) => {
    navigateCalendarToDate(newValue);
  }, [navigateCalendarToDate]);

  const handleDateDoubleClick = React.useCallback((date) => {
    const nextDate = dayjs(date);
    if (!nextDate.isValid()) return;

    setSelectedDate(nextDate);
    setActiveMode(NEW_CALENDAR_MODES.CALENDAR);
    setActivePeriod(NEW_CALENDAR_PERIODS.DAY);

    const calendarApi = fullCalendarRef.current?.getApi?.();
    const dayView = NEW_CALENDAR_VIEWS[NEW_CALENDAR_MODES.CALENDAR][NEW_CALENDAR_PERIODS.DAY];
    if (calendarApi) calendarApi.changeView(dayView, nextDate.toDate());
  }, []);

  const handleMonthChange = React.useCallback((nextMonth) => {
    const targetDate = getDateInTargetMonth(selectedDate, nextMonth);
    if (targetDate) navigateCalendarToDate(targetDate);
  }, [navigateCalendarToDate, selectedDate]);

  const handlePreviousPeriodClick = React.useCallback(() => {
    const calendarApi = fullCalendarRef.current?.getApi?.();
    if (calendarApi) calendarApi.prev();
  }, []);

  const handleNextPeriodClick = React.useCallback(() => {
    const calendarApi = fullCalendarRef.current?.getApi?.();
    if (calendarApi) calendarApi.next();
  }, []);

  const handleCalendarEventClick = React.useCallback(
    (rawTask) => {
      if (!rawTask) return;

      openTaskView(rawTask);
    },
    [openTaskView]
  );

  const handleTodayClick = React.useCallback(() => {
    const calendarApi = fullCalendarRef.current?.getApi?.();
    if (calendarApi) calendarApi.today();
  }, []);

  const changeCalendarView = React.useCallback((nextMode, nextPeriod) => {
    const calendarApi = fullCalendarRef.current?.getApi?.();
    const nextView = getViewForModeAndPeriod(nextMode, nextPeriod);
    if (calendarApi) calendarApi.changeView(nextView);
  }, []);

  const handleModeChange = React.useCallback(
    (nextMode) => {
      setActiveMode(nextMode);
      changeCalendarView(nextMode, activePeriod);
    },
    [activePeriod, changeCalendarView]
  );

  const handlePeriodChange = React.useCallback(
    (nextPeriod) => {
      setActivePeriod(nextPeriod);
      changeCalendarView(activeMode, nextPeriod);
    },
    [activeMode, changeCalendarView]
  );

  const handleCalendarDatesSet = React.useCallback((arg) => {
    const calendarDate = fullCalendarRef.current?.getApi?.().getDate?.();
    const nextDate = dayjs(calendarDate ?? arg?.view?.currentStart ?? arg?.start);
    if (nextDate.isValid()) setSelectedDate(nextDate);
    setCurrentRangeLabel(String(arg?.view?.title ?? ''));
    setVisibleRange({
      start: arg?.start ?? null,
      end: arg?.end ?? null,
    });
    const { mode, period } = getModeAndPeriodForView(arg?.view?.type);
    setActiveMode(mode);
    setActivePeriod(period);
  }, []);

  React.useEffect(() => {
    if (currentRangeLabel) return;

    const weekStart = getWeekStart(selectedDate);
    const weekEnd = weekStart.add(6, 'day');
    const sameMonth = weekStart.month() === weekEnd.month();
    const sameYear = weekStart.year() === weekEnd.year();

    if (sameMonth && sameYear) {
      setCurrentRangeLabel(`${weekStart.format('MMM D')}–${weekEnd.format('D, YYYY')}`);
      return;
    }

    if (!sameMonth && sameYear) {
      setCurrentRangeLabel(`${weekStart.format('MMM D')}–${weekEnd.format('MMM D, YYYY')}`);
      return;
    }

    setCurrentRangeLabel(`${weekStart.format('MMM D, YYYY')}–${weekEnd.format('MMM D, YYYY')}`);
  }, [currentRangeLabel, getWeekStart, selectedDate]);

  React.useEffect(() => {
    saveCalendarUiState(calendarPath, {
      activeMode,
      activePeriod,
      selectedDate: selectedDate.toISOString(),
      filters: {
        categoryChecks: catChecks,
        onlyWithHacs,
        showSystemTasks,
        advancedFilters,
      },
    });
  }, [
    activeMode,
    activePeriod,
    advancedFilters,
    calendarPath,
    catChecks,
    onlyWithHacs,
    selectedDate,
    showSystemTasks,
  ]);

  return {
    adapterLocale,
    badgeDays,
    badgeLoading,
    categories,
    catChecks,
    closeModalTask,
    closeModalTaskView,
    closeRemoveModal,
    confirmEventCompletion,
    decodedToken,
    executeDeleteTask,
    calendarEvents,
    fullCalendarRef,
    activeMode,
    activePeriod,
    handleCalendarDatesSet,
    handleCalendarEventClick,
    handleCategoryCheckChange,
    handleDateChange,
    handleDateDoubleClick,
    handleEditFromView,
    handleModeChange,
    handleMonthChange,
    handlePeriodChange,
    handleRemoveFromView,
    handleTodayClick,
    localeText,
    modalOpenTask,
    modalOpenTaskView,
    onlyWithHacs,
    openCreateTask,
    openRemoveModal,
    currentRangeLabel,
    handleNextPeriodClick,
    handlePreviousPeriodClick,
    refreshCalendar,
    removeEntityId,
    selectedDate,
    rollbackEventCompletion,
    setOnlyWithHacs,
    setShowSystemTasks,
    startEventCompletion,
    showSystemTasks,
    advancedFilters,
    setAdvancedFilters,
    pmas,
    members,
    task,
    taskView,
    taskViewMember,
  };
}
